import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, RefreshCw, Loader2, ImageOff, Link as LinkIcon, Download, Receipt } from "lucide-react";
import { Navbar } from "../components/Navbar";
import api, { formatApiErrorDetail, downloadInvoice } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

function LogoPreview({ url, code }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  if (url && !broken) {
    return <img src={url} alt={code} onError={() => setBroken(true)} data-testid="logo-preview" className="h-12 w-12 rounded-md object-contain border border-slate-200 bg-white p-1" />;
  }
  return (
    <div data-testid="logo-preview-fallback" className="h-12 w-12 rounded-md border border-slate-200 bg-slate-900 text-white flex items-center justify-center font-pnr text-sm">
      {url ? <ImageOff className="h-4 w-4" /> : (code || "—")}
    </div>
  );
}

/* ---------------- Airlines ---------------- */
function AirlinesTab() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ iata_code: "", name: "", domain: "", logo_url: "", logo_source: "" });
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/airlines`, { params: { search: q } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ iata_code: "", name: "", domain: "", logo_url: "", logo_source: "" });
    setDialogOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ iata_code: r.iata_code, name: r.name, domain: r.domain || "", logo_url: r.logo_url || "", logo_source: r.logo_source || "" });
    setDialogOpen(true);
  };

  const fetchMissing = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/admin/airlines/fetch-missing-logos`);
      if (data.processed === 0) toast.success("All airlines already have logos.");
      else toast.success(`Fetched ${data.ok} logo(s). ${data.with_logo}/${data.total} airlines now have logos.`);
      load(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  const fetchLogo = async () => {
    if (!editing) { toast.error("Save the airline first, then fetch its logo."); return; }
    setFetching(true);
    try {
      const { data } = await api.post(`/admin/airlines/${form.iata_code}/fetch-logo`);
      if (data.logo_url) {
        setForm((f) => ({ ...f, logo_url: data.logo_url, logo_source: data.airline.logo_source || "" }));
        toast.success("Logo fetched.");
      } else {
        toast.error("No logo found for this domain. You can paste a URL manually.");
        setForm((f) => ({ ...f, logo_url: "", logo_source: "" }));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setFetching(false);
    }
  };

  const pullFromUrl = async () => {
    const url = (form.logo_url || "").trim();
    if (!url) { toast.error("Enter a logo URL first."); return; }
    if (!editing) {
      setForm((f) => ({ ...f, logo_source: "url" }));
      toast.success("URL set — click Save to apply.");
      return;
    }
    setPulling(true);
    try {
      const { data } = await api.post(`/admin/airlines/${form.iata_code}/set-logo-from-url`, { logo_url: url });
      setForm((f) => ({ ...f, logo_url: data.logo_url, logo_source: data.logo_source || "url" }));
      toast.success("Logo pulled from URL.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPulling(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/airlines/${editing.iata_code}`, {
          name: form.name, domain: form.domain, logo_url: form.logo_url || null,
          logo_source: form.logo_source || null,
        });
        toast.success("Airline updated.");
      } else {
        await api.post(`/admin/airlines`, {
          iata_code: form.iata_code, name: form.name, domain: form.domain,
          logo_url: form.logo_url || null,
        });
        toast.success("Airline added.");
      }
      setDialogOpen(false);
      load(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/airlines/${deleteTarget.iata_code}`);
      toast.success("Airline deleted.");
      load(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="airline-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code or name..." className="pl-9" />
        </div>
        <Button data-testid="add-airline-btn" onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Add airline
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-500">Bulk-fetch logos for any airlines that don't have one yet (e.g. newly added).</p>
        <Button data-testid="fetch-missing-logos-btn" onClick={fetchMissing} disabled={syncing} variant="outline" className="border-slate-200 shrink-0">
          {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
          {syncing ? "Fetching logos…" : "Fetch all missing logos"}
        </Button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Logo</TableHead>
              <TableHead className="w-20">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Source</TableHead>
              <TableHead className="text-right w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No airlines found.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.iata_code} data-testid={`airline-row-${r.iata_code}`}>
                <TableCell><LogoPreview url={r.logo_url} code={r.iata_code} /></TableCell>
                <TableCell className="font-pnr font-medium">{r.iata_code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-slate-500 font-pnr text-xs">{r.domain || "—"}</TableCell>
                <TableCell className="text-slate-500 text-xs">{r.logo_source || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`edit-airline-${r.iata_code}`} variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-8 w-8 text-slate-500"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`delete-airline-${r.iata_code}`} variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} className="h-8 w-8 text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit airline" : "Add airline"}</DialogTitle>
            <DialogDescription>Manage airline details and logo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>IATA code</Label>
              <Input data-testid="airline-form-code" value={form.iata_code} disabled={!!editing} maxLength={3} onChange={(e) => setForm({ ...form, iata_code: e.target.value.toUpperCase() })} className="mt-1 font-pnr uppercase" placeholder="AA" />
            </div>
            <div>
              <Label>Name</Label>
              <Input data-testid="airline-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="American Airlines" />
            </div>
            <div>
              <Label>Domain</Label>
              <Input data-testid="airline-form-domain" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} className="mt-1 font-pnr" placeholder="aa.com" />
            </div>
            <div className="flex items-end gap-3">
              <LogoPreview url={form.logo_url} code={form.iata_code} />
              <Button data-testid="airline-fetch-logo" type="button" variant="outline" onClick={fetchLogo} disabled={fetching} className="border-slate-200">
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Fetch logo
              </Button>
            </div>
            <div>
              <Label>Logo URL (manual override)</Label>
              <div className="flex gap-2 mt-1">
                <Input data-testid="airline-form-logo-url" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value, logo_source: "url" })} className="font-pnr text-xs" placeholder="https://upload.wikimedia.org/.../logo.png" />
                <Button data-testid="airline-pull-url" type="button" variant="outline" onClick={pullFromUrl} disabled={pulling || !form.logo_url} className="border-slate-200 shrink-0">
                  {pulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LinkIcon className="h-4 w-4 mr-1.5" /> Pull from URL</>}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Paste a direct image URL and pull it to override the auto-fetched logo.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">Cancel</Button>
            <Button data-testid="airline-form-save" onClick={save} disabled={saving || !form.iata_code || !form.name} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete airline?</AlertDialogTitle>
            <AlertDialogDescription>This will remove {deleteTarget?.name} ({deleteTarget?.iata_code}). This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-airline" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Airports ---------------- */
function AirportsTab() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, matched: 0, limit: 500 });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ iata_code: "", name: "", city: "", country: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/airports`, { params: { search: q } });
      setRows(data.airports || []);
      setMeta({ total: data.total || 0, matched: data.matched || 0, limit: data.limit || 500 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const openAdd = () => { setEditing(null); setForm({ iata_code: "", name: "", city: "", country: "" }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ iata_code: r.iata_code, name: r.name, city: r.city || "", country: r.country || "" }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/airports/${editing.iata_code}`, { name: form.name, city: form.city, country: form.country });
        toast.success("Airport updated.");
      } else {
        await api.post(`/admin/airports`, form);
        toast.success("Airport added.");
      }
      setDialogOpen(false);
      load(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/airports/${deleteTarget.iata_code}`);
      toast.success("Airport deleted.");
      load(search);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="airport-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, name, city or country..." className="pl-9" />
        </div>
        <Button data-testid="add-airport-btn" onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> Add airport
        </Button>
      </div>
      <p className="text-xs text-slate-400 mb-4" data-testid="airports-count-hint">
        {meta.total.toLocaleString()} airports in the database worldwide.{" "}
        {search
          ? `Showing ${Math.min(rows.length, meta.matched).toLocaleString()} of ${meta.matched.toLocaleString()} match(es).`
          : `Showing first ${rows.length.toLocaleString()} — use search to find any airport.`}
      </p>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Country</TableHead>
              <TableHead className="text-right w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">No airports found.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.iata_code} data-testid={`airport-row-${r.iata_code}`}>
                <TableCell className="font-pnr font-medium">{r.iata_code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="text-slate-500">{r.city || "—"}</TableCell>
                <TableCell className="text-slate-500">{r.country || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`edit-airport-${r.iata_code}`} variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-8 w-8 text-slate-500"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`delete-airport-${r.iata_code}`} variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} className="h-8 w-8 text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit airport" : "Add airport"}</DialogTitle>
            <DialogDescription>Manage airport details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>IATA code</Label>
              <Input data-testid="airport-form-code" value={form.iata_code} disabled={!!editing} maxLength={3} onChange={(e) => setForm({ ...form, iata_code: e.target.value.toUpperCase() })} className="mt-1 font-pnr uppercase" placeholder="JFK" />
            </div>
            <div>
              <Label>Name</Label>
              <Input data-testid="airport-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="John F. Kennedy International Airport" />
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="airport-form-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" placeholder="New York" />
            </div>
            <div>
              <Label>Country</Label>
              <Input data-testid="airport-form-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1" placeholder="United States" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">Cancel</Button>
            <Button data-testid="airport-form-save" onClick={save} disabled={saving || !form.iata_code || !form.name} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete airport?</AlertDialogTitle>
            <AlertDialogDescription>This will remove {deleteTarget?.name} ({deleteTarget?.iata_code}). This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-airport" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------- Logo settings ---------------- */
function SettingsTab() {
  const [urlPattern, setUrlPattern] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [provider, setProvider] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/logo").then(({ data }) => {
      setUrlPattern(data.url_pattern || "");
      setApiKey(data.api_key || "");
      setProvider(data.provider || "");
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/logo", { url_pattern: urlPattern, api_key: apiKey });
      toast.success("Logo provider settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="max-w-xl">
      <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
        <div>
          <h2 className="font-heading font-bold text-slate-900">Logo provider</h2>
          <p className="text-sm text-slate-500">Current provider: <span className="font-medium">{provider || "custom"}</span>. Use <code className="font-pnr text-xs bg-slate-100 px-1 rounded">{"{domain}"}</code> and <code className="font-pnr text-xs bg-slate-100 px-1 rounded">{"{token}"}</code> placeholders in the URL pattern.</p>
        </div>
        <div>
          <Label>URL pattern</Label>
          <Input data-testid="settings-url-pattern" value={urlPattern} onChange={(e) => setUrlPattern(e.target.value)} className="mt-1 font-pnr text-xs" placeholder="https://img.logo.dev/{domain}?token={token}" />
        </div>
        <div>
          <Label>API key</Label>
          <Input data-testid="settings-api-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="mt-1 font-pnr text-xs" placeholder="pk_..." />
        </div>
        <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

const PLAN_OPTIONS = [
  { id: "basic", name: "Basic (Free)" },
  { id: "plus", name: "Plus" },
  { id: "api", name: "Conversion API" },
];
const API_TIER_OPTIONS = [
  { v: 0, name: "10,000 calls/mo" },
  { v: 1, name: "25,000 calls/mo" },
  { v: 2, name: "50,000 calls/mo" },
  { v: 3, name: "Unlimited" },
];

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
const selectCls = "w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500";

function UsersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const emptyNew = { email: "", password: "", name: "", role: "user", plan: "basic", api_tier: 0, plan_users: 1 };
  const [addOpen, setAddOpen] = useState(false);
  const [nu, setNu] = useState(emptyNew);
  const [busy, setBusy] = useState(false);
  const [manage, setManage] = useState(null); // the user row being managed
  const [mPlan, setMPlan] = useState({ plan: "basic", api_tier: 0, plan_users: 1 });
  const [mPass, setMPass] = useState("");
  const [resetLink, setResetLink] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };
  const statusLabel = (u) => u.disabled ? "Disabled" : u.cancel_at_period_end ? "Cancelling" : u.subscription_status === "active" ? "Active" : (u.plan === "basic" ? "Free" : "Inactive");
  const statusCls = (u) => u.disabled ? "bg-red-100 text-red-700" : u.cancel_at_period_end ? "bg-amber-100 text-amber-700" : u.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600";

  const filtered = rows.filter((u) => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  const createUser = async () => {
    if (!nu.email || nu.password.length < 6) { toast.error("Email and a 6+ char password are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/users", nu);
      toast.success("User created.");
      setAddOpen(false); setNu(emptyNew); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openManage = (u) => {
    setManage(u);
    setMPlan({ plan: u.plan, api_tier: u.api_tier || 0, plan_users: u.plan_users || 1 });
    setMPass(""); setResetLink("");
  };

  const savePlan = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${manage.id}/plan`, mPlan);
      toast.success("Plan updated."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const savePassword = async () => {
    if (mPass.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${manage.id}/password`, { password: mPass });
      toast.success("Password set."); setMPass("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const genResetLink = async (sendEmail) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${manage.id}/reset-link`, { base_url: window.location.origin, send_email: !!sendEmail });
      setResetLink(data.reset_link);
      toast.success(sendEmail ? (data.emailed ? "Link generated & emailed." : "Link generated (email failed — copy below).") : "Reset link generated.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleDisabled = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${manage.id}/status`, { disabled: !manage.disabled });
      toast.success(data.message);
      setManage({ ...manage, disabled: data.disabled }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-slate-900">Users</h2>
          <p className="text-sm text-slate-500">{rows.length} registered user(s). Card details are demo data.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" />
          </div>
          <Button data-testid="admin-add-user-btn" onClick={() => setAddOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white shrink-0">
            <Plus className="h-4 w-4 mr-1.5" /> Add user
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead>User Name</TableHead><TableHead>Email</TableHead><TableHead>Plan</TableHead>
                <TableHead>Status</TableHead><TableHead>Charge</TableHead><TableHead>Last Logged In</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-slate-900">{u.name}{u.role === "admin" && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-blue-600 font-bold">admin</span>}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell><span className="inline-flex rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5">{u.plan_name}</span></TableCell>
                  <TableCell><span className={`inline-flex rounded-full text-xs font-medium px-2 py-0.5 ${statusCls(u)}`}>{statusLabel(u)}</span></TableCell>
                  <TableCell className="text-slate-700">${Number(u.charge).toFixed(2)}<span className="text-xs text-slate-400">/mo</span></TableCell>
                  <TableCell className="text-slate-600">{u.last_login ? fmtDate(u.last_login) : <span className="text-slate-400">Never</span>}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`manage-user-${u.email}`} variant="outline" size="sm" className="border-slate-200" onClick={() => openManage(u)}>Manage</Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No users found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add user dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-user-dialog" className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add user</DialogTitle><DialogDescription>Create an account. It's pre-verified and can sign in immediately.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Name"><Input data-testid="nu-name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} placeholder="Jane Doe" /></Field>
            <Field label="Email"><Input data-testid="nu-email" type="email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} placeholder="user@example.com" /></Field>
            <Field label="Temporary password"><Input data-testid="nu-password" type="text" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} placeholder="At least 6 characters" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role">
                <select data-testid="nu-role" className={selectCls} value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}>
                  <option value="user">User</option><option value="admin">Admin</option>
                </select>
              </Field>
              <Field label="Plan">
                <select data-testid="nu-plan" className={selectCls} value={nu.plan} onChange={(e) => setNu({ ...nu, plan: e.target.value })}>
                  {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
            </div>
            {nu.plan === "api" && (
              <Field label="API tier">
                <select data-testid="nu-tier" className={selectCls} value={nu.api_tier} onChange={(e) => setNu({ ...nu, api_tier: Number(e.target.value) })}>
                  {API_TIER_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.name}</option>)}
                </select>
              </Field>
            )}
            {nu.plan === "plus" && (
              <Field label="Seats (users)"><Input data-testid="nu-seats" type="number" min={1} value={nu.plan_users} onChange={(e) => setNu({ ...nu, plan_users: Math.max(1, Number(e.target.value)) })} /></Field>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="nu-submit" onClick={createUser} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage user dialog */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent data-testid="manage-user-dialog" className="sm:max-w-md">
          {manage && (
            <>
              <DialogHeader>
                <DialogTitle>Manage {manage.name || manage.email}</DialogTitle>
                <DialogDescription>{manage.email} · {manage.plan_name}{manage.disabled && " · Disabled"}</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div className="space-y-2 border-b border-slate-100 pb-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Plan</p>
                  <select data-testid="m-plan" className={selectCls} value={mPlan.plan} onChange={(e) => setMPlan({ ...mPlan, plan: e.target.value })}>
                    {PLAN_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {mPlan.plan === "api" && (
                    <select data-testid="m-tier" className={selectCls} value={mPlan.api_tier} onChange={(e) => setMPlan({ ...mPlan, api_tier: Number(e.target.value) })}>
                      {API_TIER_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.name}</option>)}
                    </select>
                  )}
                  {mPlan.plan === "plus" && (
                    <Input data-testid="m-seats" type="number" min={1} value={mPlan.plan_users} onChange={(e) => setMPlan({ ...mPlan, plan_users: Math.max(1, Number(e.target.value)) })} placeholder="Seats" />
                  )}
                  <Button data-testid="m-save-plan" size="sm" onClick={savePlan} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">Update plan</Button>
                </div>

                <div className="space-y-2 border-b border-slate-100 pb-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Set new password</p>
                  <div className="flex gap-2">
                    <Input data-testid="m-password" type="text" value={mPass} onChange={(e) => setMPass(e.target.value)} placeholder="New password (6+ chars)" />
                    <Button data-testid="m-save-password" size="sm" onClick={savePassword} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white shrink-0">Set</Button>
                  </div>
                </div>

                <div className="space-y-2 border-b border-slate-100 pb-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Password reset link</p>
                  <div className="flex gap-2">
                    <Button data-testid="m-gen-link" size="sm" variant="outline" className="border-slate-200" onClick={() => genResetLink(false)} disabled={busy}><LinkIcon className="h-3.5 w-3.5 mr-1" /> Generate link</Button>
                    <Button data-testid="m-email-link" size="sm" variant="outline" className="border-slate-200" onClick={() => genResetLink(true)} disabled={busy}>Generate & email</Button>
                  </div>
                  {resetLink && (
                    <div className="flex items-center gap-2">
                      <Input data-testid="m-reset-link" readOnly value={resetLink} className="font-pnr text-xs" onFocus={(e) => e.target.select()} />
                      <Button size="sm" variant="outline" className="border-slate-200 shrink-0" onClick={() => { navigator.clipboard.writeText(resetLink); toast.success("Copied."); }}>Copy</Button>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400">Link is valid for 1 hour.</p>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{manage.disabled ? "Account disabled" : "Account active"}</p>
                    <p className="text-xs text-slate-500">{manage.disabled ? "User can't sign in." : "Disabling logs the user out immediately."}</p>
                  </div>
                  <Button data-testid="m-toggle-status" size="sm" onClick={toggleDisabled} disabled={busy} className={manage.disabled ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}>
                    {manage.disabled ? "Enable" : "Disable"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TransactionsTab() {
  const [data, setData] = useState({ transactions: [], count: 0, total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/transactions", { params: { search, plan, status } });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [search, plan, status]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };

  const getInvoice = async (t) => {
    setBusy(t.id);
    try { await downloadInvoice(t.id, `${t.invoice_no}.pdf`); }
    catch (e) { toast.error("Could not download invoice."); }
    finally { setBusy(""); }
  };

  const rows = data.transactions || [];

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-slate-900">All transactions</h2>
          <p className="text-sm text-slate-500">{data.count} charge(s) · Total collected <b className="text-slate-700">USD {Number(data.total_amount).toFixed(2)}</b>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="txn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search email, name or invoice" className="pl-9" />
          </div>
          <select data-testid="txn-plan-filter" value={plan} onChange={(e) => setPlan(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
            <option value="">All plans</option>
            <option value="plus">Plus</option>
            <option value="api">Conversion API</option>
          </select>
          <select data-testid="txn-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700">
            <option value="">All statuses</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <Table data-testid="admin-txn-table">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Invoice No.</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Card charged</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Invoice</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid={`admin-txn-row-${t.id}`}>
                  <TableCell className="text-slate-600">{fmtDate(t.date)}</TableCell>
                  <TableCell className="font-pnr text-xs text-slate-500">{t.invoice_no}</TableCell>
                  <TableCell className="font-medium text-slate-900">{t.user_name || "—"}</TableCell>
                  <TableCell className="text-slate-600">{t.user_email}</TableCell>
                  <TableCell><span className="inline-flex rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5">{t.plan_name}</span></TableCell>
                  <TableCell className="font-medium text-slate-900">{t.currency} {Number(t.amount).toFixed(2)}</TableCell>
                  <TableCell className="text-slate-600">{t.card_brand ? `${t.card_brand} •••• ${t.card_last4}` : "—"}</TableCell>
                  <TableCell><span className="inline-flex rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium px-2 py-0.5 capitalize">{t.status}</span></TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`admin-invoice-btn-${t.id}`} onClick={() => getInvoice(t)} disabled={busy === t.id} variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50">
                      {busy === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1.5" /> PDF</>}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No transactions found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Admin panel</h1>
        <p className="text-slate-500 mt-1">Manage airlines, airports, users, subscriptions, payments, and the logo provider.</p>

        <Tabs defaultValue="airlines" className="mt-8">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="airlines" data-testid="tab-airlines">Airlines</TabsTrigger>
            <TabsTrigger value="airports" data-testid="tab-airports">Airports</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Payments</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Logo settings</TabsTrigger>
          </TabsList>
          <TabsContent value="airlines" className="mt-6"><AirlinesTab /></TabsContent>
          <TabsContent value="airports" className="mt-6"><AirportsTab /></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="subscriptions" className="mt-6"><SubscriptionsTab /></TabsContent>
          <TabsContent value="transactions" className="mt-6"><TransactionsTab /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function SubscriptionsTab() {
  const [data, setData] = useState({ subscriptions: [], count: 0, active: 0, mrr: 0, currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/admin/subscriptions"); setData(data); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, []);

  const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(); } catch { return "—"; } };
  const rows = (data.subscriptions || []).filter((s) =>
    !q || `${s.name} ${s.email}`.toLowerCase().includes(q.toLowerCase()));

  const statusBadge = (s) => {
    if (s.cancel_at_period_end) return "bg-amber-100 text-amber-700";
    if (s.subscription_status === "active") return "bg-emerald-100 text-emerald-700";
    return "bg-slate-100 text-slate-600";
  };

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-heading font-bold text-slate-900">Subscriptions</h2>
          <p className="text-sm text-slate-500" data-testid="subs-summary">
            {data.count} paid account(s) · <b className="text-emerald-700">{data.active} active</b> · Est. MRR <b className="text-slate-700">{data.currency} {Number(data.mrr).toFixed(2)}</b>
          </p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="subs-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-9" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <Table data-testid="admin-subs-table">
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Billing</TableHead>
                <TableHead>Seats / Tier</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Next charge</TableHead>
                <TableHead>Subscription ID</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`admin-sub-row-${s.id}`}>
                  <TableCell className="font-medium text-slate-900">{s.name}</TableCell>
                  <TableCell className="text-slate-600">{s.email}</TableCell>
                  <TableCell><span className="inline-flex rounded-full bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5">{s.plan_name}</span></TableCell>
                  <TableCell className="capitalize text-slate-600">{s.interval}</TableCell>
                  <TableCell className="text-slate-600">{s.plan === "plus" ? `${s.seats_used}/${s.users} seats` : s.plan === "api" ? `Tier ${(s.api_tier ?? 0) + 1}` : "—"}</TableCell>
                  <TableCell className="font-medium text-slate-900">{data.currency} {Number(s.monthly_charge).toFixed(2)}</TableCell>
                  <TableCell><span className={`inline-flex rounded-full text-xs font-medium px-2 py-0.5 capitalize ${statusBadge(s)}`}>{s.cancel_at_period_end ? "cancelling" : s.subscription_status}</span></TableCell>
                  <TableCell className="text-slate-600">{s.cancel_at_period_end ? `ends ${fmtDate(s.plan_ends_at)}` : fmtDate(s.next_charge_at)}</TableCell>
                  <TableCell className="font-pnr text-xs text-slate-500">{s.razorpay_subscription_id || "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No subscriptions yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
